import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getAirconServiceCost, getNewUserDiscountPercent } from '../../../../api/aircon-service/aircon-service-api'
import { RequestContext } from '../../../../context/RequestContext'
import { UserContext } from '../../../../context/UserContext'
import { LocationContext } from '../../../../context/LocationContext'
import { getMarketByName, localiseTerms, t } from '../../../../utils/markets'
import { mountLabel } from '../../../../utils/market-labels'
import { getServiceCategorySettingsByCountryAndRequestType } from '../../../../api/request-category/request-category-api'
import { to12HourFormat } from '../../../../utils/time-converter'
import { AlertTriangle, AlertCircle, Info, Calendar, Clock, LayoutGrid, Pencil, ChevronDown } from 'lucide-react'
// Imported under another name: this component has its own closeModal below
import { closeModal as closeModalEntry, swapModal } from '../../../../service/modal-service'
import { AnimatePresence, motion } from 'framer-motion'
import { BottomSheet } from '../../../commons/BottomSheet'
import { clampServiceDate, isServiceDateOutOfRange, toDateStr } from '../../../../utils/service-date'
import {
    SERVICE_OPTIONS, shortDateLabel, shortTime, displayName,
    SegmentedControl, SectionTitle, ScrollRow, ServiceTile, ServiceChip, SelectedServiceCard,
} from './AirconSelectionParts'

// Aircon booking sheet: what -> how many / mount type -> when -> total -> Continue.
//
// All pricing, discount and request-criteria logic is unchanged from the
// previous two-modal version (schedule form + separate AddAirconItems picker).
// Only the presentation moved into one BottomSheet, the same sheet the
// handyman picker uses.

// Format date to "23 Dec 2025" format
const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const SHEET_MAX_HEIGHT = "92vh"
const ANNUAL_QUALIFYING_ITEMS = ["General Servicing", "Chemical Wash", "Chemical Overhaul"]

// Rate-factor hints from the pricing API. "cl" means the day is closed.
const RATE_FACTOR_MESSAGES = {
    late: "Selecting a time during usual business hours may be cheaper",
    emergency: "For a better price, try a weekday at least 2 days in advance",
    urgent: "High demand! A further date may be cheaper",
    sunday: "Next Sunday has high demand! Try a weekday for better price",
    weekend: "Try a weekday for better price",
    ph: "Pricing may be higher on public holidays",
    cl: "We are not operating this day",
    night: "Try booking for slots after 9am for better pricing",
    "early-morning": "Try booking for slots after 9am for better pricing"
};

const RateFactorMessage = ({ factor }) => {
    const text = RATE_FACTOR_MESSAGES[factor];
    if (!text) return null;
    const isClosed = factor === "cl";
    return (
        <div className={`flex items-start gap-2 text-xs rounded-xl px-3 py-2 border ${
            isClosed ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
            {isClosed ? <AlertCircle className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
            <span>{text}</span>
        </div>
    );
};

// "13:00", 2 -> "1pm – 3pm"
const slotRange = (start, hours) => `${shortTime(start)} – ${shortTime(addHours(start, hours))}`

const addHours = (t, hours) => {
    const [h, m] = t.split(':').map(Number)
    return `${String((h + hours) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Hourly arrival-window starts from minAllowed to maxAllowed ("9:00" or "09:00").
const buildTimeSlots = (minAllowed, maxAllowed) => {
    const [minH, minM] = minAllowed.split(':').map(Number)
    const [maxH] = maxAllowed.split(':').map(Number)
    const slots = []
    for (let h = minM > 0 ? minH + 1 : minH; h <= Math.min(maxH, 23); h++) {
        slots.push(`${String(h).padStart(2, '0')}:00`)
    }
    return slots
}

// Date and arrival window are two tap fields on one row. Each is a styled face
// over an invisible native control, so tapping opens the phone's own date
// picker or list picker. Kept local because the shared DateTimeInput and
// TimeSlotInput are used by the other booking forms.
const PickerField = ({ label, icon: Icon, text, children }) => (
    <label className="block min-w-0">
        <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</span>
        <span className="relative flex items-center gap-2 h-11 px-3 bg-white border-2 border-gray-100 rounded-xl hover:border-orange-200 focus-within:border-orange-400 transition-colors">
            <Icon className="w-4 h-4 text-orange-500 shrink-0" aria-hidden="true" />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold text-gray-900">{text}</span>
            <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />
            {children}
        </span>
    </label>
)

// text-base keeps iOS from zooming in when the (invisible) control gets focus.
const NATIVE_OVERLAY = "absolute inset-0 w-full h-full opacity-0 cursor-pointer text-base disabled:cursor-not-allowed"

export const AirconItemSelection = (props) => {
    const setIsShowAirconItemSelection = props.setIsShowAirconItemSelection
    const setIsCustomBook = props.setIsCustomBook
    const setRequestForm = props.setRequestForm
    const userId = props.userId
    // Back from the booking form reopens this sheet. Continue renames items
    // (mount type appended) before handing them on, so the parent keeps the raw
    // selection from onSelectionConfirmed and passes it back as initialSelection.
    // The /home re-book card also opens it pre-filled, from a past request
    // (utils/aircon-items.js), with keepSchedule: false for a fresh date.
    const initialSelection = props.initialSelection
    const onSelectionConfirmed = props.onSelectionConfirmed
    // Continue is a custom booking; clears a quick-book flag left over from a
    // quick-book card used earlier on the page, which would otherwise send the
    // form to the date step (and the quick-book price API) instead of submit.
    const setIsQuickBook = props.setIsQuickBook
    const isRestoring = useRef(!!initialSelection?.items?.length)
    // Back keeps the date/time already in RequestContext; a re-book starts
    // from the usual default date and time
    const keepSchedule = useRef(isRestoring.current && initialSelection?.keepSchedule !== false)

    // The customer's market: the logged-in account's country on /home, the
    // page's country on public pages. Prices come from that market's profile.
    const { userCountryName } = useContext(UserContext) ?? {}
    const { countryName: locationCountryName } = useContext(LocationContext) ?? {}
    const countryName = props.countryName || userCountryName || locationCountryName || "Singapore"
    const market = getMarketByName(countryName)
    // Sent to the price API only for non-SG markets, so SG requests are unchanged.
    const quoteCountry = market && market.countryCd !== 'sg' ? countryName : undefined
    // Labels and line prices for UAE / Malaysia (AirconSelectionParts); SG passes none.
    const labelMarket = quoteCountry ? market : undefined
    // Totals: "SGD 57.41" in Singapore, "AED 119.60" / "RM 89.24" elsewhere.
    const currencyLabel = labelMarket ? labelMarket.currencyPrefix.trim() : (market?.currency ?? "SGD")
    const requestType = "Aircon Services";

    const [ frequency, setFrequency ] = useState(initialSelection?.frequency || "Only once");
    const [ annualServiceCount, setAnnualServiceCount ] = useState(initialSelection?.annualServiceCount || "4");
    const [ isRecalculate, setIsRecalculate ] = useState(false)

    const [ isCalculateCost, setIsCalculateCost ] = useState(false)
    // Inline loading state (replaces the old full-screen LoadingCover):
    // isLoading - whole-list recalculation after a schedule change
    // addingItem - the service tile currently being priced and added
    // updatingIndex - the row whose units or mount type is being re-priced
    const [ isLoading, setIsLoading ] = useState(false)
    const [ addingItem, setAddingItem ] = useState(null)
    const [ updatingIndex, setUpdatingIndex ] = useState(null)

    // Vertical-space management for small phones (e.g. Galaxy S8+, 360x740):
    // isPickerExpanded - full tile grid until the first add, then a one-row chip strip
    // highlightIndex - the just-added card, scrolled into view and ringed briefly
    // isWhenHighlighted - flashes the "When?" section when the footer summary is tapped
    const [ isPickerExpanded, setIsPickerExpanded ] = useState(!isRestoring.current)
    const [ highlightIndex, setHighlightIndex ] = useState(null)
    const [ isWhenHighlighted, setIsWhenHighlighted ] = useState(false)
    // Restored items should not be treated as just added (collapse + scroll + ring)
    const prevItemCount = useRef(isRestoring.current ? initialSelection.items.length : 0)
    const whenRef = useRef(null)

    const currentDate = new Date().toLocaleString( 'sv', { timeZoneName: 'short' } ).substring(0,10)
    const serviceHours = 2
    const [ minAllowed, setMinAllowed ] = useState("09:00")
    const [ maxAllowed, setMaxAllowed ] = useState("19:00")
    const [ minDate, setMinDate ] = useState("")
    const [ maxDate, setMaxDate ] = useState("")
    const [ discountPercent, setDiscountPercent ] = useState(0)
    const [ discount, setDiscount ] = useState(0)
    const [ rateAggregatorFactor, setRateAggregatorFactor ] = useState("")

    const {
            setRequestCriteria,
            serviceItems,
            setServiceItems,
            serviceDate,
            setServiceDate,
            startTime,
            setStartTime,
            endTime,
            setEndTime,
            serviceCost,
            setServiceCost,
            setTotalDiscountPercent,
            originalCost,
            setOriginalCost,
            setRequestDescription,
            setServiceSummary,
            setServiceUnitsCount
        } = useContext(RequestContext);

    const getServiceType = (newItem) => {
        const serviceTypes = {
            "General Servicing": "General servicing for cleaning",
            "Chemical Wash": "Chemical cleaning",
            "Chemical Overhaul": "Chemical cleaning",
            "Condenser Cleaning": "Condenser cleaning",
            "Condenser Chem. Wash": "Condenser cleaning",
            "Drain Pipe Flush": "Drain pipe flush",
            "Gas Refill Half Tank": "Gas top up only",
            "Gas Refill Full Tank": "Gas top up only",
            "Aircon Repair (est.)": "Aircon repair (water leaking, not cooling, no power etc.)"
        };
        return serviceTypes[newItem] || "";
    }

    const getServiceSubType = (newItem) => {
        const serviceSubTypes = {
            "Chemical Overhaul": "Chemical overhaul",
            "Condenser Cleaning": "Normal cleaning",
            "Condenser Chem. Wash": "Chemical cleaning",
            "Gas Refill Half Tank": "Not sure",
            "Gas Refill Full Tank": "Full tank refill"
        };
        return serviceSubTypes[newItem] || "";
    }

    const getServiceCost = useCallback((serviceType, unitCount, serviceSubType, mountType) => {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await getAirconServiceCost(userId, serviceType, unitCount, serviceSubType, 0, serviceDate, mountType, startTime, endTime, frequency, annualServiceCount + " times/year", quoteCountry)
                setDiscountPercent(response.discountPercent)
                setRateAggregatorFactor(response.rateAggregatorFactor)
                resolve(response.servicingCost)
            } catch (error) {
                console.log(error)
                setIsLoading(false)
                reject(0)
            }
        })
    }, [serviceDate, endTime, startTime, frequency, annualServiceCount, userId, quoteCountry])

    const handleFrequencyChange = (value) => {
        setFrequency(value);
        if(serviceItems.length > 0) setIsRecalculate(true)
    }

    const handleAnnualServiceCountChange = (value) => {
        setAnnualServiceCount(value);
        if(serviceItems.length > 0) setIsRecalculate(true)
    }

    const handleServiceDateChange = (e) => {
        // iOS date picker ignores min/max, so clamp the selection into the allowed range
        const selectedDate = clampServiceDate(e.target.value, minDate, maxDate)
        if(!selectedDate) return
        setServiceDate(selectedDate)
        if(selectedDate === currentDate){
            const min = new Date().getHours() + 1
            setMinAllowed(min + ":00")
            setMaxAllowed("23:00")
        } else {
            setMinAllowed("09:00")
            setMaxAllowed("19:00")
        }
        if(serviceItems.length > 0) setIsRecalculate(true)
    }

    const handleStartTimeChange = (e) => {
        setStartTime(e.target.value)
    }

    const timeSlots = useMemo(() => buildTimeSlots(minAllowed, maxAllowed), [minAllowed, maxAllowed])

    // Arrival windows start on the hour. Snap anything else (a time left out of
    // range by a date change, or a half-hour from an older booking) to the next
    // available slot so the picker always shows a selection.
    useEffect(() => {
        if (!startTime || timeSlots.length === 0 || timeSlots.includes(startTime)) return
        setStartTime(timeSlots.find((t) => t > startTime) || timeSlots[timeSlots.length - 1])
    }, [startTime, timeSlots, setStartTime])

    const updateItemCost = async (index, updateCallback) => {
        setUpdatingIndex(index)
        try {
            const updatedItems = await Promise.all(
                serviceItems.map(async (item, i) => {
                    if (i === index) {
                        const updatedItem = { ...item };
                        updateCallback(updatedItem);

                        const serviceType = getServiceType(updatedItem.itemName);
                        const serviceSubType = getServiceSubType(updatedItem.itemName);

                        if (serviceType) {
                            const cost = await getServiceCost(serviceType, updatedItem.unitCount, serviceSubType, updatedItem.mountType);
                            updatedItem.itemCost = parseFloat(cost);
                        }
                        return updatedItem;
                    }
                    return item;
                })
            );

            setServiceItems(updatedItems);
            setIsCalculateCost(true);
        } catch (error) {
            console.error("Error updating item cost:", error);
        } finally {
            setUpdatingIndex(null)
        }
    };

    const handleUnitsChange = async (target) => {
        const { index, unitCount } = target;
        if (unitCount === 0) {
            removeItem(index);
            return;
        }
        await updateItemCost(index, (item) => { item.unitCount = unitCount; });
    };

    const handleMountTypeChange = async (target) => {
        const { index, mountType } = target;
        if (serviceItems[index]?.mountType === mountType) return;
        await updateItemCost(index, (item) => { item.mountType = mountType; });
    };

    const addItem = async (newItem) => {
        const editableItems = ["General Servicing", "Chemical Wash", "Chemical Overhaul", "Condenser Cleaning", "Condenser Chem. Wash", "Drain Pipe Flush", "Gas Refill Custom Qty", "Full Tank Gas Refill"];
        const defaultMountTypes = ["General Servicing", "Chemical Wash", "Chemical Overhaul", "Aircon Repair (est.)"];
        const mountType = defaultMountTypes.includes(newItem) ? "Wall" : "";

        const serviceType = getServiceType(newItem);
        const serviceSubType = getServiceSubType(newItem);

        setAddingItem(newItem)
        try {
            const newItemCost = await getServiceCost(serviceType, 1, serviceSubType, mountType);
            const item = {
                itemName: newItem,
                mountType,
                unitCount: 1,
                itemCost: parseFloat(newItemCost),
                isEditable: editableItems.includes(newItem),
            };
            setServiceItems(prevItems => [...prevItems, item]);
            setIsCalculateCost(true);
        } catch (error) {
            console.error("Error adding item:", error);
        } finally {
            setAddingItem(null)
        }
    };

    const recalculateCost = useCallback(async () => {
        if(!isRecalculate) return
        setIsRecalculate(false);
        setIsLoading(true)
        try {
            const updatedItems = await Promise.all(
                serviceItems.map(async (item) => {
                    const serviceType = getServiceType(item.itemName);
                    const serviceSubType = getServiceSubType(item.itemName);
                    if (!serviceType) return item;
                    const itemCost = await getServiceCost(serviceType, item.unitCount, serviceSubType, item.mountType);
                    return { ...item, itemCost: parseFloat(itemCost) };
                })
            );
            setServiceItems(updatedItems);
            setIsCalculateCost(true);
        } catch (error) {
            console.error("Error recalculating cost:", error);
        } finally {
            setIsLoading(false)
        }
    }, [serviceItems, getServiceCost, isRecalculate, setServiceItems]);

    // Immutable removal. The old version spliced the context array in place
    // before setting it.
    const removeItem = (index) => {
        setServiceItems(items => items.filter((_, i) => i !== index))
        setIsCalculateCost(true)
    }

    useEffect(() => {
        if (!startTime || !serviceHours) return;
        const [minHour, minMinutes] = startTime.split(":").map(Number);
        const serviceDuration = parseFloat(serviceHours);
        let hour = minHour + Math.floor(serviceDuration);
        let minutes = minMinutes + (serviceDuration % 1) * 60;
        if (minutes >= 60) { minutes -= 60; hour += 1; }
        if (hour >= 24) hour -= 24;
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinutes = minutes.toString().padStart(2, "0");
        setEndTime(`${formattedHour}:${formattedMinutes}`);
        if(serviceItems.length > 0) setIsRecalculate(true)
    }, [startTime, serviceHours, setEndTime, serviceItems.length]);

    useEffect(() => {
        if(!countryName || !requestType) return

        const getServiceCategorySettings = async() => {
            try {
                const serviceSetting = await getServiceCategorySettingsByCountryAndRequestType(countryName, requestType)
                const minD = new Date()
                if(minD.getHours() > 21) minD.setDate(minD.getDate() + (serviceSetting.nextSlotAdvance + 1))
                else minD.setDate(minD.getDate() + serviceSetting.nextSlotAdvance)

                setMinDate(toDateStr(minD))

                const maxD = new Date()
                maxD.setDate(maxD.getDate() + 182)
                setMaxDate(toDateStr(maxD))

                // Coming back from the booking form: keep the date and time the
                // customer already picked (still in RequestContext).
                if (keepSchedule.current) return

                const sDate = new Date()
                sDate.setDate(sDate.getDate() + 2)
                // setDate(minD.getDate()) only copies the day of month and can land
                // before minD when minD rolls over to the next month
                if(minD > sDate) sDate.setTime(minD.getTime())
                if(sDate.getDay() === 0) sDate.setDate(sDate.getDate() + 1)
                if(sDate.getDay() === 6) sDate.setDate(sDate.getDate() + 2)

                setServiceDate(toDateStr(sDate))

                setStartTime("13:00")
                setEndTime("15:00")
                // Re-book: price the restored services for this date
                if (isRestoring.current) setIsRecalculate(true)
            } catch (error) {
                console.log(error)
            }
        }
        getServiceCategorySettings();
    }, [setServiceDate, setStartTime, setEndTime, countryName, requestType])

    const getNewUserDiscount = useCallback(async(totalCost) => {
        try {
            const newUserDiscountPercent = await getNewUserDiscountPercent(userId, totalCost.toFixed(2), quoteCountry)
            if(totalCost > 0){
                const discount = discountPercent > 0 ? totalCost * (discountPercent + newUserDiscountPercent)/100 : 0
                setTotalDiscountPercent((discountPercent + newUserDiscountPercent).toFixed(2));
                setDiscount(discount.toFixed(2))
                setServiceCost((totalCost - discount).toFixed(2))
            } else {
                setTotalDiscountPercent(0);
                setDiscount(0)
                setServiceCost(0)
            }
        } catch (error) {
            console.log(error)
        }
    }, [discountPercent, setServiceCost, setTotalDiscountPercent, userId, quoteCountry])

    useEffect(() => {
        if (!isCalculateCost || !discountPercent) return;
        const totalCost = serviceItems.reduce((acc, item) => acc + item.itemCost, 0);
        setOriginalCost(totalCost.toFixed(2));
        const oneTimeServices = ["Gas Refill Half Tank", "Gas Refill Full Tank", "Condenser Cleaning", "Condenser Chem. Wash", "Aircon Repair (est.)"];
        // UAE / Malaysia: the summary appended to the remarks uses the market's
        // names ("AC Service - One-time | AC Cleaning Split (wall) x2").
        const itemText = (item) => labelMarket
            ? `${displayName(item.itemName, labelMarket)}${item.mountType ? ` ${mountLabel(item.mountType, labelMarket)}` : ""} x${item.unitCount}`
            : `${item.itemName}${item.mountType ? ` ${item.mountType}` : ""} x${item.unitCount}`;
        const serviceWord = labelMarket ? t(labelMarket, 'acCategory') : "Aircon service";
        const serviceSummary = frequency === "Annual package"
            ? `${serviceWord} - Annual package ${annualServiceCount} times/year | ${serviceItems.map((item) => {
                const prefix = oneTimeServices.includes(item.itemName) ? "One-time " : "";
                return `${prefix}${itemText(item)}`;
              }).join(" | ")}`
            : `${serviceWord} - One-time | ${serviceItems.map(itemText).join(" | ")}`;
        setServiceSummary(serviceSummary);
        getNewUserDiscount(totalCost);
        setIsCalculateCost(false);
    }, [isCalculateCost, discountPercent, serviceItems, annualServiceCount, frequency, getNewUserDiscount, setOriginalCost, setIsCalculateCost, setServiceSummary, labelMarket]);

    useEffect(() => {
        if(!isRecalculate) return
        recalculateCost()
    }, [isRecalculate, recalculateCost])

    const confirmBook = (e) => {
        e?.preventDefault?.();
        // Safety net - iOS allows picking a date outside the min/max range
        if (isServiceDateOutOfRange(serviceDate, minDate, maxDate)) {
            setServiceDate(clampServiceDate(serviceDate, minDate, maxDate))
            if (serviceItems.length > 0) setIsRecalculate(true)
            return;
        }
        onSelectionConfirmed?.({ items: serviceItems, frequency, annualServiceCount })

        const targetNames = ["General Servicing", "Chemical Wash", "Chemical Overhaul"];
        let serviceUnitsCount = serviceItems.reduce((count, item) => targetNames.includes(item.itemName) ? count + item.unitCount : count, 0);
        if (serviceUnitsCount === 0) serviceUnitsCount = 1;

        const processedServiceItems = serviceItems.map((item) => ({
            ...item,
            itemName: targetNames.includes(item.itemName) ? `${item.itemName} ${item.mountType || ""}`.trim() : item.itemName
        }));

        setServiceItems(processedServiceItems);
        setServiceUnitsCount(serviceUnitsCount);

        const requestCriteria = [
            ...processedServiceItems,
            { itemName: `Frequency of service - ${frequency}`, [21]: frequency },
            { itemName: `Requested service date - ${new Date(serviceDate).toDateString()}`, [99]: serviceDate },
            { itemName: `Service time slot - ${to12HourFormat(startTime)} to ${to12HourFormat(endTime)}`, [100]: to12HourFormat(startTime) + " to " + to12HourFormat(endTime) }
        ];

        if (frequency === "Annual package") {
            requestCriteria.push({ itemName: `Annual package type - ${annualServiceCount} times/year`, [23]: annualServiceCount });
        }

        setRequestCriteria(requestCriteria);
        setIsQuickBook?.(false);
        setIsCustomBook(true);
        // Hands this sheet's history entry to the form when it has one
        // (opened with openModal); otherwise opens the form with a new entry.
        swapModal(setIsShowAirconItemSelection, setRequestForm);
    };

    const reset = useCallback(() => {
        setServiceItems([])
        setServiceCost(0)
        setOriginalCost(0)
        setTotalDiscountPercent(0)
        setDiscount(0)
        setRateAggregatorFactor("")
        setIsCustomBook(false)
        setRequestDescription("")
        setServiceSummary("")
    }, [setServiceItems, setServiceCost, setOriginalCost, setTotalDiscountPercent, setDiscount, setRateAggregatorFactor, setIsCustomBook, setRequestDescription, setServiceSummary])

    const closeModal = () => {
        window.scrollTo({ top: 0 });
        reset()
        closeModalEntry(setIsShowAirconItemSelection)
    }

    // Fresh sheet: clear any earlier selection. Reopened from the booking form:
    // put back the raw items (the context holds the renamed ones) and let the
    // start-time effect re-price them for the kept date and time.
    useEffect(() => {
        if (!isRestoring.current) {
            reset()
            return
        }
        setServiceItems(initialSelection.items)
        setServiceSummary("")
        // The booking form appended the old summary to the remarks; it gets
        // the new one when the customer continues again.
        setRequestDescription("")
        setIsCustomBook(false)
        // Re-book waits for the default date (settings fetch above) before
        // pricing, rather than pricing against no date
        if (keepSchedule.current) setIsRecalculate(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset])

    const isDateInvalid = isServiceDateOutOfRange(serviceDate, minDate, maxDate);
    const isBusy = isLoading || addingItem !== null || updatingIndex !== null
    const hasQualifyingItem = serviceItems.some(item => ANNUAL_QUALIFYING_ITEMS.includes(item.itemName))
    const isAnnualInvalid = frequency === "Annual package" && !hasQualifyingItem

    const canSubmit = serviceCost > 0 && !isDateInvalid && rateAggregatorFactor !== "cl" && !isAnnualInvalid && !isBusy;

    // Why Continue is disabled, in priority order. Previously the button was
    // simply hidden with no explanation.
    const submitHint = serviceItems.length === 0
        ? "Add a service to continue"
        : isDateInvalid
        ? "Pick a valid date"
        : rateAggregatorFactor === "cl"
        ? "We're not operating on this date – pick another"
        : isAnnualInvalid
        ? (labelMarket
            ? `Annual package needs ${t(labelMarket, 'acGeneral')}, ${t(labelMarket, 'acChemicalWash')} or ${t(labelMarket, 'acChemicalOverhaul')}`
            : "Annual package needs General Servicing, Chemical Wash or Chemical Overhaul")
        : isBusy || !(serviceCost > 0)
        ? "Updating price…"
        : null

    const addedCount = (itemName) => serviceItems.filter((item) => item.itemName === itemName).length
    const totalUnits = serviceItems.reduce((sum, item) => sum + (item.unitCount || 0), 0)

    // When a service is added: collapse the tile grid into the chip strip, then
    // scroll the new card into view and ring it briefly. On a 360x740 phone the
    // card used to land below the fold with no visible feedback.
    useEffect(() => {
        const count = serviceItems.length
        const prev = prevItemCount.current
        prevItemCount.current = count
        if (count === 0) {
            setIsPickerExpanded(true)
            return
        }
        if (count <= prev) return
        const index = count - 1
        setIsPickerExpanded(false)
        setHighlightIndex(index)
        // Wait for the collapse and the new card to render before scrolling.
        const scrollTimer = setTimeout(() => {
            document.getElementById(`aircon-item-${index}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }, 150)
        const clearTimer = setTimeout(() => setHighlightIndex(null), 1600)
        return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer) }
    }, [serviceItems.length])

    // Footer summary tap: bring "When?" into view and flash it.
    const showWhen = () => {
        whenRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
        setIsWhenHighlighted(true)
        setTimeout(() => setIsWhenHighlighted(false), 1600)
    }

    // Kept short so the footer summary always fits on one line at 360px.
    const shortDate = serviceDate ? shortDateLabel(serviceDate) : 'Pick a date'
    const timeRange = startTime && endTime ? `${shortTime(startTime)}–${shortTime(endTime)}` : ''
    const frequencyLabel = frequency === "Annual package" ? `${annualServiceCount}×/yr` : "One-time"

    return (
        <BottomSheet isOpen={true} onClose={closeModal} maxWidth="lg" maxHeight={SHEET_MAX_HEIGHT} disableDrag showDragHandle={false}>
            {/* Own flex column instead of BottomSheet.Content: Content reserves a
                fixed 166px for header + footer, and this footer (summary line +
                total + Continue) is taller than that, so with a few services added
                the footer was pushed past the sheet's bottom edge and clipped.
                Here the middle section takes whatever height is left. */}
            <div className="flex flex-col" style={{ maxHeight: SHEET_MAX_HEIGHT }}>
            <BottomSheet.Header className="py-2.5! shrink-0">
                <BottomSheet.Title>{localiseTerms('Book Aircon Service', labelMarket)}</BottomSheet.Title>
                <BottomSheet.Subtitle>Add services & time – Instant pricing</BottomSheet.Subtitle>
            </BottomSheet.Header>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-auto">
                <div className="px-3 py-3 space-y-4">
                    {/* 1. What */}
                    <section aria-labelledby="aircon-what">
                        <SectionTitle step={1}><span id="aircon-what">Add service</span></SectionTitle>
                        {isPickerExpanded ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {SERVICE_OPTIONS.map((itemName) => (
                                    <ServiceTile
                                        key={itemName}
                                        itemName={itemName}
                                        addedCount={addedCount(itemName)}
                                        isAdding={addingItem === itemName}
                                        disabled={addingItem !== null || isLoading}
                                        onAdd={addItem}
                                        market={labelMarket}
                                    />
                                ))}
                            </div>
                        ) : (
                            // One scrollable row once something is added; ScrollRow shows
                            // a chevron and peeks sideways so the other services are found.
                            <ScrollRow itemLabel="services" nudge className="pt-1.5 pb-1">
                                {SERVICE_OPTIONS.map((itemName) => (
                                    <ServiceChip
                                        key={itemName}
                                        itemName={itemName}
                                        addedCount={addedCount(itemName)}
                                        isAdding={addingItem === itemName}
                                        disabled={addingItem !== null || isLoading}
                                        onAdd={addItem}
                                        market={labelMarket}
                                    />
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setIsPickerExpanded(true)}
                                    className="shrink-0 flex items-center gap-1.5 min-h-10 px-3 rounded-full border border-dashed border-gray-300 text-[13px] font-medium text-gray-600 whitespace-nowrap"
                                >
                                    <LayoutGrid className="w-3.5 h-3.5" aria-hidden="true" />
                                    All services
                                </button>
                            </ScrollRow>
                        )}
                    </section>

                    {/* 2. Selected services */}
                    {serviceItems.length > 0 && (
                        <section aria-labelledby="aircon-selected">
                            <SectionTitle step={2}><span id="aircon-selected">Your services</span></SectionTitle>
                            <div className="space-y-2">
                                <AnimatePresence initial={false}>
                                    {serviceItems.map((item, i) => (
                                        <SelectedServiceCard
                                            key={item.itemName + i}
                                            item={item}
                                            index={i}
                                            isHighlighted={highlightIndex === i}
                                            isUpdating={updatingIndex === i || isLoading}
                                            disabled={isBusy}
                                            onUnitsChange={handleUnitsChange}
                                            onMountTypeChange={handleMountTypeChange}
                                            market={labelMarket}
                                        />
                                    ))}
                                </AnimatePresence>
                            </div>
                            {serviceItems.some(item => item.itemName === "Aircon Repair (est.)") && (
                                <div className="mt-2 flex items-start gap-2 text-xs rounded-xl px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
                                    <Info className="w-4 h-4 shrink-0" />
                                    <span>Technician will diagnose the issue. Service proceeds only with your approval of extra cost.</span>
                                </div>
                            )}
                        </section>
                    )}

                    {/* 3. When */}
                    <section
                        ref={whenRef}
                        aria-labelledby="aircon-when"
                        className={`space-y-3 scroll-mt-2 rounded-2xl transition-colors duration-500 ${isWhenHighlighted ? 'bg-orange-50 ring-2 ring-orange-200 -mx-1 px-1 py-1' : ''}`}
                    >
                        <SectionTitle step={serviceItems.length > 0 ? 3 : 2}><span id="aircon-when">When?</span></SectionTitle>
                        <div className="flex flex-wrap items-center gap-2">
                            <SegmentedControl
                                ariaLabel="How often"
                                value={frequency}
                                onChange={handleFrequencyChange}
                                options={[
                                    { value: "Only once", label: "One-time" },
                                    { value: "Annual package", label: "Annual" },
                                ]}
                            />
                            {frequency === "Annual package" && (
                                <SegmentedControl
                                    ariaLabel="Services per year"
                                    value={annualServiceCount}
                                    onChange={handleAnnualServiceCountChange}
                                    options={[
                                        { value: "2", label: "2×/yr" },
                                        { value: "3", label: "3×/yr" },
                                        { value: "4", label: "4×/yr" },
                                    ]}
                                />
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <PickerField label="Date" icon={Calendar} text={serviceDate ? shortDateLabel(serviceDate) : 'Loading…'}>
                                <input
                                    type="date"
                                    value={serviceDate || ''}
                                    min={minDate}
                                    max={maxDate}
                                    onChange={handleServiceDateChange}
                                    onClick={(e) => { try { e.currentTarget.showPicker?.() } catch { /* already open */ } }}
                                    disabled={!minDate}
                                    aria-label="Service date"
                                    className={NATIVE_OVERLAY}
                                />
                            </PickerField>
                            <PickerField label={`Arrival (${serviceHours}-hr slot)`} icon={Clock} text={startTime ? slotRange(startTime, serviceHours) : 'Select'}>
                                <select
                                    value={startTime || ''}
                                    onChange={handleStartTimeChange}
                                    aria-label="Arrival window"
                                    className={NATIVE_OVERLAY}
                                >
                                    {timeSlots.map((start) => (
                                        <option key={start} value={start}>{slotRange(start, serviceHours)}</option>
                                    ))}
                                </select>
                            </PickerField>
                        </div>
                        {isDateInvalid && minDate && (
                            <div className="flex items-start gap-2 text-xs rounded-xl px-3 py-2 bg-red-50 border border-red-200 text-red-700">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span>Earliest available service date is {formatDisplayDate(minDate)}. Please select a valid date.</span>
                            </div>
                        )}
                        <RateFactorMessage factor={rateAggregatorFactor} />
                    </section>
                </div>
            </div>

            {/* Sticky footer: booking summary + total + Continue, always visible,
                so the date, time and unit count are in view whenever Continue is tapped. */}
            <BottomSheet.Footer className="shrink-0 pt-2.5! pb-[max(0.625rem,env(safe-area-inset-bottom))]!">
                <button
                    type="button"
                    onClick={showWhen}
                    aria-label={`Booking for ${shortDate}${timeRange ? `, ${timeRange}` : ''}, ${frequencyLabel}, ${totalUnits} units. Change date or time`}
                    className="w-full flex items-center gap-1.5 min-h-8 mb-1 text-sm text-gray-700 text-left whitespace-nowrap overflow-hidden"
                >
                    <Calendar className="w-3.5 h-3.5 text-orange-500 shrink-0" aria-hidden="true" />
                    <span className="font-semibold whitespace-nowrap">{shortDate}</span>
                    {timeRange && (
                        <>
                            <Clock className="w-3.5 h-3.5 text-orange-500 shrink-0 ml-1" aria-hidden="true" />
                            <span className="font-semibold whitespace-nowrap">{timeRange}</span>
                        </>
                    )}
                    <span className="text-gray-400">·</span>
                    <span className="whitespace-nowrap font-semibold">{frequencyLabel}</span>
                    {/* {totalUnits > 0 && (
                        <>
                            <span className="text-gray-400">·</span>
                            <span className="font-semibold whitespace-nowrap">{totalUnits} {totalUnits === 1 ? 'unit' : 'units'}</span>
                        </>
                    )} */}
                    <Pencil className="w-3.5 h-3.5 text-orange-500 ml-auto shrink-0" aria-hidden="true" />
                </button>
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0" aria-live="polite">
                        {serviceCost > 0 ? (
                            <>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-lg font-bold text-gray-900 tabular-nums">{currencyLabel} {serviceCost}</span>
                                    {discount > 0 && (
                                        <span className="text-xs text-gray-400 line-through tabular-nums">{currencyLabel} {originalCost}</span>
                                    )}
                                </div>
                                <div className={`text-xs ${submitHint ? 'text-gray-500' : 'text-green-600'}`}>
                                    {submitHint || (discount > 0 ? `You save ${currencyLabel} ${discount}` : 'Total estimate')}
                                </div>
                            </>
                        ) : (
                            <div className="text-xs text-gray-500">{submitHint}</div>
                        )}
                    </div>
                    <motion.button
                        type="button"
                        onClick={confirmBook}
                        disabled={!canSubmit}
                        className="shrink-0 min-h-12 px-6 text-sm font-bold text-white bg-linear-to-r from-orange-500 to-amber-500 rounded-xl shadow-lg shadow-orange-200 disabled:from-gray-300 disabled:to-gray-300 disabled:shadow-none transition-colors"
                        whileTap={canSubmit ? { scale: 0.98 } : undefined}
                    >
                        Continue
                    </motion.button>
                </div>
            </BottomSheet.Footer>
            </div>
        </BottomSheet>
    )
}
